/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Input } from '@angular/core';
import { FeedbackStats, GeneratedReportFeedbackStatsSection, Organization } from '@app/app/shared/models';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';

@Component({
  selector: 'ptl-feedback-stats-report-learner-section',
  templateUrl: './stats-section.component.html',
  styleUrls: ['./stats-section.component.scss'],
})
export class StatsSectionComponent {
  @Input() section: GeneratedReportFeedbackStatsSection;

  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;
  protected readonly isNaN = isNaN;
  protected readonly MIN_USERS_FOR_BENCHMARK = 5;

  constructor(private translocoService: TranslocoService) {}

  isInvalidValue(value: unknown): boolean {
    return value === null || value === undefined || value === 'NaN' || (typeof value === 'number' && Number.isNaN(value));
  }

  formatNumber(value: unknown, digits: string = '1.0-0'): string {
    if (this.isInvalidValue(value)) return 'NaN';
    if (typeof value !== 'number') return String(value);

    const { minFraction, maxFraction } = this.parseDigitsFormat(digits);
    return value.toLocaleString(undefined, {
      minimumFractionDigits: minFraction,
      maximumFractionDigits: maxFraction,
    });
  }

  formatPercent(value: unknown, digits: string = '1.0-0'): string {
    if (this.isInvalidValue(value)) return 'NaN';
    return `${this.formatNumber(value as number, digits)}%`;
  }

  isPositiveChange(value: unknown): boolean | null {
    if (this.isInvalidValue(value)) return null;
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return null;
    if (num > 0) return true;
    if (num < 0) return false;
    return null;
  }

  getTooltip(stats: FeedbackStats): string {
    if (!stats) return '';
    switch (stats.type) {
      case 'AVG_RATING':
        return this.translocoService.translate('translations.diagnostics.feedbackStats.avgRatingTooltip');
      case 'CONSENSUS':
        return this.translocoService.translate('translations.diagnostics.feedbackStats.consensusTooltip');
      case 'AVG_SCORE':
        return this.translocoService.translate('translations.diagnostics.feedbackStats.avgScoreTooltip');
      default:
        return '';
    }
  }

  private parseDigitsFormat(digits: string): { minFraction: number; maxFraction: number } {
    const [, fractionPart = '0-0'] = digits.split('.');
    const [min = '0', max = '0'] = fractionPart.split('-');
    return { minFraction: Number(min), maxFraction: Number(max) };
  }
}
